/**
 * Feature-gate map for the admin-hub items (#67e P2, 2026-07-08).
 *
 * The 21 advanced admin routes gated in `TimesheetRoutesProvider` (behind the
 * `timesheet.*` bundles that default OFF) still have nav entries in
 * `adminGroups.tsx`. Without nav-hiding, a de-selected feature shows in the
 * Admin & Reporting hub and lands on the "Feature not enabled" notice — worse,
 * it sits next to a working sibling (e.g. "Data Residency Runtime" beside the
 * built "Data Residency"), reading as a broken duplicate.
 *
 * This maps each gated admin path → its feature key so the hub can drop items
 * whose feature isn't built into this deployment. Keyed on the FULL
 * `/timesheet/...` path used by `adminGroups.tsx`. Ungated carve-outs
 * (submission-packages, labor-distribution, indirect-labor, my-utilization,
 * settings) are intentionally absent. Route→bundle rationale:
 * Tasks/Feature_Route_Map_Timesheet_Advanced_2026-07-08.
 */
import { isFeatureBuilt } from '@/config/featureCatalog'

const ADMIN_PATH_FEATURE: Record<string, string> = {
  '/timesheet/attendance-flags': 'timesheet.anomaly',
  '/timesheet/predictive-flags': 'timesheet.anomaly',
  '/timesheet/wand-attempts': 'timesheet.anomaly',
  '/timesheet/compliance': 'timesheet.compliance',
  '/timesheet/compliance-posture': 'timesheet.compliance',
  '/timesheet/workforce-compliance': 'timesheet.compliance',
  '/timesheet/evidence-collection': 'timesheet.compliance',
  '/timesheet/fedramp-engagement': 'timesheet.compliance',
  '/timesheet/cert-reminders': 'timesheet.compliance',
  '/timesheet/data-residency-runtime': 'timesheet.data-governance',
  '/timesheet/customer-managed-keys': 'timesheet.data-governance',
  '/timesheet/retention-purge': 'timesheet.data-governance',
  '/timesheet/dot-eld-hos': 'timesheet.dot-eld',
  '/timesheet/eld-adapters': 'timesheet.dot-eld',
  '/timesheet/multi-state-tax': 'timesheet.multi-state-tax',
  '/timesheet/mes-bridge': 'timesheet.mfg-integrations',
  '/timesheet/order-status': 'timesheet.mfg-integrations',
  '/timesheet/earned-vs-actual': 'timesheet.mfg-integrations',
  '/timesheet/integrations-dashboard': 'timesheet.mfg-integrations',
  '/timesheet/notification-dispatch': 'timesheet.mfg-integrations',
  '/timesheet/training-schedule': 'timesheet.training',
  // Defaults ON (see featureCatalog), so this hides the nav item only in a fork
  // that explicitly opts OUT of Time Evaluation via VITE_FEATURES.
  '/timesheet/time-evaluation': 'timesheet.time-evaluation',
}

/**
 * Is this admin path built into the deployment? An ungated path (not in the
 * map) is always built; a gated path follows its feature key. Fail-open by
 * construction — unmapped paths and unknown keys resolve to built.
 */
export function isAdminPathBuilt(path: string): boolean {
  const feature = ADMIN_PATH_FEATURE[path]
  return feature ? isFeatureBuilt(feature) : true
}
